import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useActivePlayback } from '../../context/ActivePlaybackContext';
import {
  Speaker,
  LayoutDashboard,
  Settings,
  Bug,
  Library,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Music,
  Info,
  Monitor,
  Link2,
  Archive,
  Inbox,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import OnboardingTour from '../onboarding/OnboardingTour';
import SidebarVisualizer from '../../visualizer/controls/SidebarVisualizer';
import { getEventsByDjId } from '../../services/events';
import { getRequestsByEventId, updateRequest, matchedLinkFields, SongRequest } from '../../services/requests';
import { LEGACY_PLAY_MODE, playModeOf, type PlayMode } from '../../services/playModes';
import { isWaitingOnCoApprover, shouldRouteToCoApprover, staleCoApprovalFields } from '../../utils/coApprovalRouting';
import { SongSearchResult } from '../../services/itunesApi';
import { isrcForMatchedSong } from '../../utils/songIdentity';
import SongMatchModal from '../dj/SongMatchModal';
import { MatchRun, MatchRunStep, startMatchRun, advanceMatchRun } from '../../utils/matchRun';
import { getCoApprovalDjSession } from '../../services/coApprovalDj';
import { useReviewHold } from '../../hooks/useReviewHold';
import { holderLabel, isHeldByOther } from '../../utils/reviewHold';
import { ReviewHoldBanner } from '../dj/ReviewHoldBanner';
import { useBridgeLauncher } from '../../hooks/useBridgeLauncher';
import { BridgeLaunchNotice } from '../dj/BridgeLaunchNotice';
import { LegalLinks } from '../legal/LegalLinks';

const ONBOARDING_KEY = 'votebeats_onboarding_completed';

type PendingAcrossEvents = SongRequest & {
  _eventName: string;
  _coApprovalEnabled: boolean;
  _playMode: PlayMode;
  _coApprovalDjActive: boolean;
};

async function isCoApprovalDjActive(eventId: string, djId: string): Promise<boolean> {
  try {
    return (await getCoApprovalDjSession(eventId, djId))?.status === 'active';
  } catch (error) {
    console.error(`[DJLayout] Could not read the co-approval DJ link for event ${eventId}; no holds for it:`, error);
    return false;
  }
}

interface MatchRunNavigationState {
  matchRunRequestIds?: string[];
}

export default function DJLayout() {
  const { user, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const { activePlayback } = useActivePlayback();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { state: bridgeLaunchState, launch: launchBridge, recheck: recheckBridge, dismiss: dismissBridgeLaunch } = useBridgeLauncher(activePlayback?.eventId);

  // Cross-event Process Pending state
  const [allPendingRequests, setAllPendingRequests] = useState<PendingAcrossEvents[]>([]);
  const [matchingRequest, setMatchingRequest] = useState<PendingAcrossEvents | null>(null);
  const [pendingMatchIndex, setPendingMatchIndex] = useState(0);
  const [pendingMatchTotal, setPendingMatchTotal] = useState(0);
  const [matchRun, setMatchRun] = useState<MatchRun | null>(null);
  const [matchRunIncludesCurrentEvent, setMatchRunIncludesCurrentEvent] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const reviewHold = useReviewHold({
    eventId: undefined,
    enabled: false,
    onExpired: () => {
      handleCloseMatchModal();
      toast('Your hold ran out — the song is back on the list', { icon: '⏱️' });
    },
  });

  // Detect current event manage route
  const currentManageEventId = (() => {
    const m = /^\/events\/([^/]+)\/manage/.exec(location.pathname);
    return m ? m[1] : null;
  })();

  const filteredPendingRequests = currentManageEventId
    ? allPendingRequests.filter(r => r.eventId !== currentManageEventId)
    : allPendingRequests;
  const pendingCount = filteredPendingRequests.length;
  const pendingButtonLabel = currentManageEventId
    ? `Process Other Pending (${pendingCount})`
    : `Process Pending (${pendingCount})`;

  // Load cross-event pending requests
  const loadPendingRequests = useCallback(async (): Promise<PendingAcrossEvents[] | null> => {
    if (!user?.uid) return null;
    try {
      const events = await getEventsByDjId(user.uid);
      const activeEvents = events.filter(e => e.status !== 'completed' && !e.archived);
      const allPending: PendingAcrossEvents[] = [];
      await Promise.all(activeEvents.map(async (event) => {
        try {
          const [requests, coApprovalDjActive] = await Promise.all([
            getRequestsByEventId(event.id),
            isCoApprovalDjActive(event.id, user.uid),
          ]);
          // Songs already with the co-approver are theirs to decide, as on the Pending page
          // Mirrored by isWaitingOnDj in functions/src/utils/djPendingAlerts.ts, which decides the pending-requests email
          const coApprovalEnabled = event.settings.coApprovalEnabled || false;
          const pending = requests.filter(r =>
            r.status === 'pending' && !r.superRequest && !isWaitingOnCoApprover(coApprovalEnabled, r.coApprovalStatus)
          );
          const eventPlayMode = playModeOf(event).id;
          for (const p of pending) {
            allPending.push({ ...p, _eventName: event.name, _coApprovalEnabled: coApprovalEnabled, _playMode: eventPlayMode, _coApprovalDjActive: coApprovalDjActive });
          }
        } catch (error) {
          console.error(`[DJLayout] Could not load pending requests for event ${event.id}:`, error);
        }
      }));
      return allPending;
    } catch (error) {
      console.error('[DJLayout] Could not load pending requests:', error);
      return null;
    }
  }, [user?.uid]);

  useEffect(() => {
    let cancelled = false;
    loadPendingRequests().then(allPending => {
      if (!cancelled && allPending) setAllPendingRequests(allPending);
    });
    return () => { cancelled = true; };
  }, [loadPendingRequests]);

  // After an import, the settings or create page hands over the unmatched songs to walk through (ITER-121)
  const matchRunRequestIds = (location.state as MatchRunNavigationState | null)?.matchRunRequestIds;
  useEffect(() => {
    if (!matchRunRequestIds || matchRunRequestIds.length === 0) return;
    navigate(location.pathname, { replace: true, state: null });
    loadPendingRequests().then(allPending => {
      if (!allPending) {
        toast.error('Could not load the imported songs to match');
        return;
      }
      setAllPendingRequests(allPending);
      const wanted = new Set(matchRunRequestIds);
      const runRequests = allPending.filter(r => wanted.has(r.id));
      if (runRequests.length === 0) return;
      const run = startMatchRun(runRequests.map(r => r.id));
      setMatchRunIncludesCurrentEvent(true);
      void openNextInRun({ run, nextId: run.remainingIds[0], position: 1 }, runRequests, new Set(runRequests.map(r => r.id)));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchRunRequestIds]);

  // Check if onboarding should be shown for this user
  useEffect(() => {
    if (user?.uid) {
      const completedOnboarding = localStorage.getItem(`${ONBOARDING_KEY}_${user.uid}`);
      if (!completedOnboarding) {
        // Small delay to let the dashboard load first
        const timer = setTimeout(() => setShowOnboarding(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [user?.uid]);

  const handleOnboardingComplete = () => {
    if (user?.uid) {
      localStorage.setItem(`${ONBOARDING_KEY}_${user.uid}`, 'true');
      // Also update user doc for persistence across devices
      const userDocs = JSON.parse(localStorage.getItem('votebeats_users_db') || '{}');
      if (userDocs[user.uid]) {
        userDocs[user.uid].onboardingCompleted = true;
        localStorage.setItem('votebeats_users_db', JSON.stringify(userDocs));
      }
    }
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    // Even if skipped, mark as completed so it doesn't show again
    handleOnboardingComplete();
  };

  const handleCloseMatchModal = () => {
    reviewHold.release();
    setMatchingRequest(null);
    setPendingMatchIndex(0);
    setPendingMatchTotal(0);
    setMatchRun(null);
    setMatchRunIncludesCurrentEvent(false);
  };

  // Each event's co-approval DJ may hold a song; those are skipped (ITER-194)
  const openNextInRun = async (step: MatchRunStep, runPool: PendingAcrossEvents[], stillPendingIds: ReadonlySet<string>) => {
    let current = step;
    while (current.nextId) {
      const nextId = current.nextId;
      const next = runPool.find(r => r.id === nextId);
      if (next) {
        const outcome = await reviewHold.claim(next.id, { eventId: next.eventId, enabled: next._coApprovalDjActive });
        if (outcome.status === 'error') toast.error("Couldn't reserve this song — the helper may open it too");
        if (outcome.status === 'held') {
          toast(`${holderLabel(outcome.heldBy)} is reviewing this one`, { icon: '👀' });
        } else {
          setMatchRun(current.run);
          setMatchingRequest(next);
          setPendingMatchIndex(current.position);
          setPendingMatchTotal(current.run.total);
          return;
        }
      }
      current = advanceMatchRun(current.run, nextId, stillPendingIds);
    }
    handleCloseMatchModal();
  };

  const advanceToNextPending = () => {
    if (!matchRun || !matchingRequest) {
      handleCloseMatchModal();
      return;
    }
    const runPool = matchRunIncludesCurrentEvent ? allPendingRequests : filteredPendingRequests;
    const stillPendingIds = new Set(runPool.map(r => r.id));
    void openNextInRun(advanceMatchRun(matchRun, matchingRequest.id, stillPendingIds), runPool, stillPendingIds);
  };

  const handleProcessPending = () => {
    const available = filteredPendingRequests.filter(r => !isHeldByOther(r, 'dj', Date.now(), r._coApprovalDjActive));
    if (available.length === 0) return;
    const run = startMatchRun(available.map(r => r.id));
    void openNextInRun({ run, nextId: run.remainingIds[0], position: 1 }, available, new Set(available.map(r => r.id)));
  };

  const handleModalApproveWithPlatformLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: SongSearchResult | null,
    durationOverrideMs: number | null,
    durationVerified: 'exact' | 'approximate' | null,
  ) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = allPendingRequests.find(r => r.id === requestId);
      const routeToCoApprover = shouldRouteToCoApprover(existing?._coApprovalEnabled ?? false, existing?.coApprovalStatus);
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { status: 'queued' }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        ...(platformName === 'Spotify' ? { preppedInSpotify: true } : {}),
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      if (itunesResult) {
        updateData.song = {
          ...existing?.song,
          title: itunesResult.trackName,
          artist: itunesResult.artistName,
          albumArtUrl: itunesResult.artworkUrl100 || existing?.song?.albumArtUrl || '',
          durationMs: itunesResult.trackTimeMillis || existing?.song?.durationMs || 0,
          explicitFlag: itunesResult.trackExplicitness === 'explicit' || existing?.song?.explicitFlag || false,
          itunesTrackId: String(itunesResult.trackId),
          isrc: isrcForMatchedSong(itunesResult, existing?.song ?? {}),
          previewUrl: itunesResult.previewUrl || existing?.song?.previewUrl || null,
        };
        for (const key of Object.keys(updateData.song as Record<string, unknown>)) {
          if ((updateData.song as Record<string, unknown>)[key] === undefined) {
            (updateData.song as Record<string, unknown>)[key] = null;
          }
        }
      }
      if (durationOverrideMs || durationVerified) {
        if (!updateData.song && existing) updateData.song = { ...existing.song };
        updateData.durationVerified = durationVerified || null;
        if (durationOverrideMs && updateData.song) {
          (updateData.song as Record<string, unknown>).durationMs = durationOverrideMs;
        }
      }
      const songForLink = (updateData.song as SongRequest['song'] | undefined) ?? existing?.song;
      Object.assign(updateData, songForLink
        ? matchedLinkFields({ platformLink, platformName, songLinkUrl }, songForLink)
        : { platformLink, platformName, ...(songLinkUrl ? { songLinkUrl } : {}) });
      await updateRequest(requestId, updateData);
      setAllPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success(routeToCoApprover ? `Matched on ${platformName} — sent to co-approver for review` : `Matched on ${platformName} — added to queue`);
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
    if (pendingMatchTotal > 0) advanceToNextPending();
    else handleCloseMatchModal();
  };

  const handleModalApproveDirect = async (requestId: string) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = allPendingRequests.find(r => r.id === requestId);
      const routeToCoApprover = shouldRouteToCoApprover(existing?._coApprovalEnabled ?? false, existing?.coApprovalStatus);
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { status: 'queued' }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      await updateRequest(requestId, updateData);
      setAllPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success(routeToCoApprover ? 'Sent to co-approver for review' : 'Request approved');
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
    if (pendingMatchTotal > 0) advanceToNextPending();
    else handleCloseMatchModal();
  };

  const handleModalReject = async (requestId: string, reason: string) => {
    try {
      const existing = allPendingRequests.find(r => r.id === requestId);
      await updateRequest(requestId, {
        status: 'rejected',
        rejectionReason: reason,
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          rejected: new Date().toISOString(),
        },
      });
      setAllPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Request rejected');
    } catch {
      toast.error('Failed to reject');
    }
    if (pendingMatchTotal > 0) advanceToNextPending();
    else handleCloseMatchModal();
  };

  const handleModalDefer = async (requestId: string) => {
    await updateRequest(requestId, { djNotes: 'Researching — needs platform match' });
    toast('Marked as "Researching" — come back to approve later', { icon: '🔍' });
    if (pendingMatchTotal > 0) advanceToNextPending();
    else handleCloseMatchModal();
  };

  const handleModalSkip = () => {
    if (pendingMatchTotal > 0) advanceToNextPending();
    else handleCloseMatchModal();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      >
        Skip to main content
      </a>

      {/* Mobile header */}
      <header className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
        <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <Speaker className="w-8 h-8 text-primary" />
          <span className="text-xl font-bold">VoteBeats</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900
            border-r border-gray-200 dark:border-gray-800 transform transition-transform
            lg:transform-none flex flex-col h-screen
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Logo */}
          <Link to="/dashboard" className="hidden lg:flex items-center gap-2 p-6 hover:opacity-80 transition-opacity">
            <Speaker className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold">VoteBeats</span>
          </Link>

          {/* Navigation — scrollable area */}
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto min-h-0">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Dashboard</span>
            </NavLink>

            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </NavLink>

            <NavLink
              to="/archive"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Archive className="w-5 h-5" />
              <span>Archive</span>
            </NavLink>

            <NavLink
              to="/song-library"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Library className="w-5 h-5" />
              <span>Song Library</span>
            </NavLink>

            <NavLink
              to="/match-library"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Link2 className="w-5 h-5" />
              <span>Match Library</span>
            </NavLink>

            <a
              href="https://docs.google.com/forms/d/1e2eM5KZWUHgqJty3IBCI5DrKtws3_Nd9wUFcK14fcdM/edit"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-item"
              onClick={() => setSidebarOpen(false)}
            >
              <Bug className="w-5 h-5" />
              <span>Bug Reports</span>
            </a>

            <NavLink
              to="/about"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Info className="w-5 h-5" />
              <span>About</span>
            </NavLink>

            {/* Desktop Helper launcher — uses votebeats:// custom protocol */}
            <button
              className="nav-item w-full"
              onClick={launchBridge}
              title="Launch VoteBeats Bridge"
            >
              <Monitor className="w-5 h-5" />
              <span>VoteBeats Bridge</span>
            </button>
            <BridgeLaunchNotice
              state={bridgeLaunchState}
              onDismiss={dismissBridgeLaunch}
              onRecheck={recheckBridge}
              onDownload={() => setSidebarOpen(false)}
            />
            <LegalLinks newTab className="px-4 pt-3 text-xs text-gray-400 dark:text-gray-500" />
          </nav>

          {/* Now Playing indicator */}
          {activePlayback && (
            <div className="flex-shrink-0 px-4 pb-2">
              <Link
                to={`/events/${activePlayback.eventId}/manage/queue`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                onClick={() => setSidebarOpen(false)}
                title="Jump to playing event"
              >
                <Music className="w-4 h-4 text-green-500 animate-pulse flex-shrink-0" />
                <span className="text-xs font-medium text-green-700 dark:text-green-400 truncate">{activePlayback.eventName}</span>
              </Link>
            </div>
          )}

          {/* Visualizer widget — pinned above bottom section */}
          <div className="flex-shrink-0">
            <SidebarVisualizer />
          </div>

          {/* Bottom section */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-800">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="nav-item w-full mb-2"
            >
              {resolvedTheme === 'dark' ? (
                <>
                  <Sun className="w-5 h-5" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>

            {/* User info and logout */}
            <div className="flex items-center justify-between">
              <Link
                to="/settings"
                className="truncate hover:text-primary transition-colors"
                title="Go to Settings"
              >
                <p className="text-sm font-medium truncate">{user?.email}</p>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-danger rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main id="main-content" className="flex-1 p-4 lg:p-8 min-h-screen min-w-0 overflow-x-hidden" tabIndex={-1}>
          {pendingCount > 0 && (
            <div className="mb-4">
              <button
                onClick={handleProcessPending}
                className="btn bg-green-500 text-white hover:bg-green-600 flex items-center gap-2"
              >
                <Inbox className="w-5 h-5" />
                {pendingButtonLabel}
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Onboarding Tour for first-time users */}
      {showOnboarding && (
        <OnboardingTour
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      <div {...reviewHold.activityHandlers}>
      <SongMatchModal
        request={matchingRequest}
        eventName={matchingRequest?._eventName}
        batchIndex={pendingMatchIndex}
        batchTotal={pendingMatchTotal}
        playMode={matchingRequest?._playMode ?? LEGACY_PLAY_MODE}
        djUid={user?.uid || ''}
        onApproveWithPlatformLink={handleModalApproveWithPlatformLink}
        onApproveDirect={handleModalApproveDirect}
        onReject={handleModalReject}
        onDefer={handleModalDefer}
        onSkip={handleModalSkip}
        onClose={handleCloseMatchModal}
        holdBanner={<ReviewHoldBanner warning={reviewHold.warning} onKeep={reviewHold.renew} />}
      />
      </div>
    </div>
  );
}
